import { useEffect, useMemo, useRef, useState } from 'react';

export interface PartyState {
	Current: number;
	Threshold: number;
	Enabled: boolean;
	Exceeded: boolean;
	HideAutoRotate: boolean;
	HideEveryEvent: boolean;
	ShowAlways: boolean;
	Level?: number;
}

export interface PausingMessage {
	ShouldPause: boolean;
	ShouldShow: boolean;
}

export interface SkipMessage {
	ShouldPause: boolean;
	EventID?: string;
}

export interface InteractionMessage {
	Available: boolean;
}

export const useEventStream = ({
	authKey,
	onConnecting,
	onChaosMessage,
	onPausingMessage,
	onSkipMessage,
	onInteractionMessage,
	onSettingsMessage,
}: {
	authKey?: string;
	onConnecting?: () => Promise<void> | void;
	onChaosMessage?: () => Promise<void> | void;
	onPausingMessage?: (payload: PausingMessage) => void;
	onSkipMessage?: (payload: SkipMessage) => void;
	onInteractionMessage?: (payload: InteractionMessage) => void;
	onSettingsMessage?: () => void;
}) => {
	const [connected, setConnected] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [partyState, setPartyState] = useState<PartyState | null>(null);
	const [showPartyGoalExceeded, setShowPartyGoalExceeded] = useState(false);
	const [showParty, setShowParty] = useState(false);

	const partyStatePercent = useMemo(() => {
		if (!partyState) {
			return null;
		}
		return Math.min(100, Math.round((partyState.Current / partyState.Threshold) * 100));
	}, [partyState]);

	// hide after 10s again
	useTimeout(
		() => {
			if (partyState && !partyState.ShowAlways) {
				setShowParty(false);
			}
		},
		showParty ? 10_000 : null,
		[partyState],
	);

	// show every 5min (300s)
	useTimeout(
		() => {
			if (partyState && !partyState.ShowAlways && !partyState.HideAutoRotate) {
				console.log('showing from rotation');
				setShowParty(true);
			}
		},
		!showParty ? 300_000 : null,
		[partyState],
	);

	useEffect(() => {
		if (showPartyGoalExceeded) {
			// Wait for the animation to finish
			setTimeout(() => {
				setShowPartyGoalExceeded(false);
			}, 10800);
		}
	}, [showPartyGoalExceeded]);

	useEffect(() => {
		if (!authKey) {
			return;
		}
		let shouldConnect = true;
		let socket: WebSocket;
		const initWebsocket = async () => {
			if (socket) {
				socket.close();
			}
			if (!shouldConnect) {
				return;
			}
			try {
				console.log('Connecting to Chaos');
				if (onConnecting) {
					await onConnecting();
				}
				if (!shouldConnect) {
					return;
				}
				socket = new WebSocket(`${import.meta.env.VITE_CHAOS_WS || 'wss://chaosgoalcoordinator-staging.tangia.workers.dev'}/ws/${authKey}`);
			} catch (ex) {
				setError('Could not connect to Chaos ' + ex);
				await new Promise((r) => setTimeout(r, 5000));
			}

			socket.onopen = () => {
				console.log('Connected to Chaos');
				setConnected(true);
			};
			socket.onmessage = (evt) => {
				const data = JSON.parse(evt.data);
				if (data.type === 'chaos_state') {
					console.log('Chaos state', data.payload);

					// Resolve whether to show
					let shouldShow = false;
					const currentPercent = partyStatePercent ?? 0;
					const newPercent = Math.round((data.payload.Current / data.payload.Threshold) * 100);
					const thesholds = [25, 50, 75, 100];
					const crossedThresholds = partyStatePercent !== null && thesholds.some((t) => t > currentPercent && t <= newPercent);
					if (data.payload.ShowAlways) {
						console.log('from show always');
						shouldShow = true;
					} else if (crossedThresholds) {
						console.log('from crossed thresholds');
						shouldShow = true;
					} else if (data.payload.Exceeded) {
						console.log('from exceeded');
						shouldShow = true;
					} else if (!data.payload.HideEveryEvent && !data.onConnect) {
						console.log('from hide every event');
						shouldShow = true;
					}

					setPartyState(null);
					setPartyState(data.payload);
					if (!showParty && shouldShow) {
						console.log('setting should show', shouldShow);
						setShowParty(shouldShow);
					}

					if (onChaosMessage) {
						onChaosMessage();
					}

					if (data.payload.Exceeded && !showPartyGoalExceeded) {
						setShowPartyGoalExceeded(true);
					}
				} else if (data.type === 'interaction_pause' && onPausingMessage) {
					/* { ShouldPause bool } */
					onPausingMessage(data.payload);
				} else if (data.type === 'interaction_skip' && onSkipMessage) {
					onSkipMessage(data.payload);
				} else if (data.type === 'interactions' && onInteractionMessage) {
					onInteractionMessage(data.payload);
				} else if (data.type === 'settings' && onSettingsMessage) {
					onSettingsMessage();
				} else {
					console.log('Unknown ws message type:', data.type);
				}
			};

			let reconnecting = false;
			const reconnect = async (e: Event) => {
				setConnected(false);
				setError('Could not connect to Chaos');
				console.log('reconnecting after', e);

				// Reconnect every 5 seconds
				// Wait 5 seconds before reconnecting
				await new Promise((r) => setTimeout(r, 5000));
				if (shouldConnect && !reconnecting) {
					reconnecting = true;
					initWebsocket();
				}
			};
			socket.onclose = reconnect;
			socket.onerror = reconnect;
		};
		initWebsocket();
		return () => {
			console.log('disconnecting from Chaos');
			shouldConnect = false;
			socket?.close();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [authKey]);

	return {
		connected,
		error,
		partyState,
		partyStatePercent,
		showPartyGoalExceeded,
		showParty,
	};
};

function useTimeout(callback: Function, delay: number | null, otherDependencies?: any[]) {
	const savedCallback = useRef(callback);
	savedCallback.current = callback;

	// Set up the interval.
	useEffect(() => {
		function tick() {
			if (savedCallback.current) {
				savedCallback.current();
			}
		}

		if (delay !== null) {
			let id = setTimeout(tick, delay);
			return () => clearTimeout(id);
		}
	}, [delay, ...(otherDependencies ?? [])]);
}
